import { Link as RouterLink } from "react-router-dom";
import {
  Text,
  Badge,
  Button,
  Box,
  Link,
  Flex,
  Image,
  Spacer,
  SimpleGrid,
  HStack,
  VStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Spinner,
} from "@chakra-ui/react";
import MyImage from "./images/mrcai4.png";

import { useAuth } from "./AuthContext";
import Auth0Login from "./Auth0Login";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";

const Navbar = () => {
  const { user, logout } = useAuth();
  const { logout: auth0Logout } = useAuth0();

  const isUserActive = user?.platformUser?.isActive === "true";
  const isUserSuperAdmin = user?.platformUser?.isSuperAdmin === "true";
  const isUserAdmin = user?.platformUser?.isAdmin === "true";
  const isUserDeveloper = user?.platformUser?.isDeveloper === "true";
  const userRoles = user?.platformUser?.roles || [];
  const isProductDesigner = userRoles.some(role =>
    (typeof role === 'string' ? role : role.role) === 'product_designer'
  );
  const navigate = useNavigate();

  const handleLogout = () => {
    logout(); // Clear local auth context
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    }); // Clear Auth0 session
  };

  // Fetch organization names for user roles
  const [orgNames, setOrgNames] = useState({});
  const [loadingOrgs, setLoadingOrgs] = useState(true);

  useEffect(() => {
    const fetchOrgNames = async () => {
      const uniqueOrgIds = [...new Set(userRoles.map((r) => r.organisationId))];
      const names = {};

      try {
        await Promise.all(
          uniqueOrgIds.map(async (orgId) => {
            try {
              const res = await fetch(
                `https://directory.poc.mrc.ai/api/parties/${orgId}`
              );
              if (res.ok) {
                const data = await res.json();
                names[orgId] = data.name || "Unknown";
              }
            } catch (err) {
              console.error(`Failed to fetch org ${orgId}:`, err);
              names[orgId] = "Unknown";
            }
          })
        );
        setOrgNames(names);
      } catch (err) {
        console.error("Failed to fetch organization names:", err);
      } finally {
        setLoadingOrgs(false);
      }
    };

    if (userRoles.length > 0) {
      fetchOrgNames();
    } else {
      setLoadingOrgs(false);
    }
  }, [userRoles]);

  return (
    <Flex p={4} color="white" align="center">
      <Box display="flex" alignItems="center">
        <Link color="white" as={RouterLink} to="/" mr={4}>
          <Image
            src={MyImage}
            height="50px"
            alt="Logo"
            objectFit="contain"
            top="0"
            left="0"
            mr={4}
          />
        </Link>
        {isUserActive && !isProductDesigner && (
          <Link color="#666262" as={RouterLink} to="/Poc" mr={4}>
            <Text fontSize="xl" color="#666262" fontWeight="bold">
              PoC
            </Text>
          </Link>
        )}
        {isUserActive && !isProductDesigner && (
          <Link color="#666262" as={RouterLink} to="/studio" mr={4}>
            <Text fontSize="xl" color="#666262" fontWeight="bold">
              Studio
            </Text>
          </Link>
        )}
        {isUserActive && !isProductDesigner && (
          <Link color="#666262" as={RouterLink} to="/placing" mr={4}>
            <Text fontSize="xl" color="#666262" fontWeight="bold">
              Placing
            </Text>
          </Link>
        )}
        {isProductDesigner && (
          <Link color="#666262" as={RouterLink} to="/designer" mr={4}>
            <Text fontSize="xl" color="#666262" fontWeight="bold">
              Designer
            </Text>
          </Link>
        )}
        {(isUserAdmin || isUserSuperAdmin) && (
          <Link color="#666262" as={RouterLink} to="/admin" mr={4}>
            <Text fontSize="xl" color="#666262" fontWeight="bold">
              Admin
            </Text>
          </Link>
        )}
        {isUserAdmin && (
          <Link
            color="#666262"
            as={RouterLink}
            to="/technical"
            mr={4}>
            <Text fontSize="xl" color="#666262" fontWeight="bold">
              Technical
            </Text>
          </Link>
        )}
        {isUserSuperAdmin && (
          <Link color="#666262" as={RouterLink} to="/Codex" mr={4}>
            <Text fontSize="xl" color="#666262" fontWeight="bold">
              Codex
            </Text>
          </Link>
        )}
        {isUserSuperAdmin && (
          <Link color="#666262" as={RouterLink} to="/Sources" mr={4}>
            <Text fontSize="xl" color="#666262" fontWeight="bold">
              Sources
            </Text>
          </Link>
        )}
        {(isUserSuperAdmin || isProductDesigner) && (
          <Link color="#666262" as={RouterLink} to="/Codify" mr={4}>
            <Text fontSize="xl" color="#666262" fontWeight="bold">
              Codify
            </Text>
          </Link>
        )}
        {isUserSuperAdmin && (
          <Link color="#666262" as={RouterLink} to="/Panner" mr={4}>
            <Text fontSize="xl" color="#666262" fontWeight="bold">
              PannerV1
            </Text>
          </Link>
        )}
        {isUserSuperAdmin && (
          <Link color="#666262" as={RouterLink} to="/panner-tree" mr={4}>
            <Text fontSize="xl" color="#666262" fontWeight="bold">
              PannerV2
            </Text>
          </Link>
        )}
      </Box>
      <Spacer />
      <Box>
        {user ? (
          <Flex align="center" ml="auto">
            <VStack>
              <HStack>
                <Badge
                  bg="#0077b5"
                  color="white"
                  mr={2}
                  fontSize="0.8em"
                  fontWeight="bold">
                  Environment : Proof Of Concept
                </Badge>
              </HStack>

              <HStack>
                {isUserDeveloper && (
                  <Badge
                    colorScheme="blue"
                    mr={2}
                    fontSize="0.8em"
                    fontWeight="bold">
                    Developer
                  </Badge>
                )}
                {isUserAdmin && (
                  <Badge
                    colorScheme="purple"
                    mr={2}
                    fontSize="0.8em"
                    fontWeight="bold">
                    Admin
                  </Badge>
                )}
                {isUserSuperAdmin && (
                  <Badge
                    colorScheme="orange"
                    mr={2}
                    fontSize="0.8em"
                    fontWeight="bold">
                    Super Admin
                  </Badge>
                )}
              </HStack>

              {userRoles.length > 0 && (
                <HStack>
                  {loadingOrgs ? (
                    <Spinner size="sm" color="teal.500" />
                  ) : userRoles.length <= 2 ? (
                    // Show roles directly if 2 or fewer
                    userRoles.map((roleObj, index) => {
                      const orgName =
                        orgNames[roleObj.organisationId] || "Unknown";
                      return (
                        <Badge
                          key={index}
                          colorScheme="teal"
                          mr={2}
                          fontSize="0.7em"
                          fontWeight="semibold">
                          {roleObj.role} @ {orgName}
                        </Badge>
                      );
                    })
                  ) : (
                    // Show popover if more than 2 roles
                    <Popover placement="bottom-end">
                      <PopoverTrigger>
                        <Badge
                          colorScheme="teal"
                          mr={2}
                          fontSize="0.7em"
                          fontWeight="semibold"
                          cursor="pointer"
                          _hover={{ opacity: 0.8 }}>
                          {userRoles.length} Roles
                        </Badge>
                      </PopoverTrigger>
                      <PopoverContent>
                        <PopoverArrow />
                        <PopoverCloseButton />
                        <PopoverHeader fontWeight="bold">
                          User Roles
                        </PopoverHeader>
                        <PopoverBody>
                          <VStack align="start" spacing={1}>
                            {userRoles.map((roleObj, index) => {
                              const orgName =
                                orgNames[roleObj.organisationId] || "Unknown";
                              return (
                                <Badge
                                  key={index}
                                  colorScheme="teal"
                                  fontSize="0.7em"
                                  fontWeight="semibold">
                                  {roleObj.role} @ {orgName}
                                </Badge>
                              );
                            })}
                          </VStack>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  )}
                </HStack>
              )}
            </VStack>

            <Image
              src={user.picture}
              alt="Profile"
              boxSize="48px"
              borderRadius="full"
              mr={2}
            />
            <Button colorScheme="red" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </Flex>
        ) : (
          <Auth0Login />
        )}
      </Box>
    </Flex>
  );
};

export default Navbar;
